import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Download,
  Search,
  Eye,
  FilePenLine,
  Trash2,
  X,
  TrendingUp,
  Wallet,
  ShoppingCart,
  Users,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getAllSales,
  deleteAdminSaleById,
  exportSalesToCSV,
  getSalesFilterOptions,
  resolveAssetUrl,
} from "../../../services/api";
import { useNotification } from "../../../hooks/useNotification";
import { DataTable } from "../Table";
import Modal from "../Modal/Modal";
import ConfirmDialog from "../components/ConfirmDialog";
import SaleCorrectionDrawer from "./SaleCorrectionDrawer";
import { localizeApiError } from "../../../utils/i18nErrorMap";
import { useCurrencyFormatter } from "../../../utils/formatCurrency";
import "./SalesReportsView.css";

const toMs = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSafeString = (value) =>
  String(value || "")
    .toLowerCase()
    .trim();

const getSaleItems = (sale) => (Array.isArray(sale?.items) ? sale.items : []);

const getSaleCategorySortValue = (sale) => {
  const categories = getSaleItems(sale)
    .map((item) => item?.product?.productGroup?.product_group_description || "")
    .filter(Boolean);
  return toSafeString([...new Set(categories)].join(" | "));
};

const getSaleProductSortValue = (sale) => {
  const products = getSaleItems(sale)
    .map(
      (item) => item?.product?.product_description || item?.product_name || "",
    )
    .filter(Boolean);
  return toSafeString([...new Set(products)].join(" | "));
};

const getSaleVariantSortValue = (sale) => {
  const variants = getSaleItems(sale)
    .map((item) => {
      const formatted = [
        item?.product?.colour,
        item?.product?.product_model_description,
      ]
        .filter(Boolean)
        .join(" / ");
      return formatted || item?.serial_number || "";
    })
    .filter(Boolean);
  return toSafeString([...new Set(variants)].join(" | "));
};

const getSaleQtySortValue = (sale) =>
  getSaleItems(sale).reduce(
    (sum, item) => sum + (Number.parseFloat(item?.quantity) || 0),
    0,
  );

const isReturnSale = (sale) => {
  const total = Number.parseFloat(sale?.total_amount);
  if (Number.isFinite(total) && total < 0) return true;

  return getSaleItems(sale).some(
    (item) => Number.parseFloat(item?.quantity) < 0,
  );
};

const getSaleTypeLabel = (sale) => (isReturnSale(sale) ? "Return" : "Sale");

const toFilterArray = (value) => {
  const rawValues = Array.isArray(value) ? value : value ? [value] : [];
  const seen = new Set();

  return rawValues
    .map((entry) => String(entry || "").trim())
    .filter(Boolean)
    .filter((entry) => {
      const key = entry.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const normalizeOptionText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const dedupeFilterOptions = (type, options = []) => {
  if (!Array.isArray(options)) return [];

  if (type === "location") {
    const byValue = new Map();
    options.forEach((option) => {
      const value = normalizeOptionText(option?.value);
      if (!value) return;
      const label = normalizeOptionText(option?.label) || value;
      const key = value.toLowerCase();

      const hasDetail = label.includes("(") && label.includes(")");
      const hasExtraInfo = label.toLowerCase() !== key;
      const score = (hasDetail ? 2 : 0) + (hasExtraInfo ? 1 : 0);
      const existing = byValue.get(key);

      if (
        !existing ||
        score > existing.score ||
        (score === existing.score && label.length > existing.label.length)
      ) {
        byValue.set(key, { value, label, score });
      }
    });

    return Array.from(byValue.values())
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      )
      .map(({ value, label }) => ({ value, label }));
  }

  const seen = new Set();
  return options
    .map((option) => {
      const value = normalizeOptionText(option?.value);
      const label = normalizeOptionText(option?.label) || value;
      return { value, label };
    })
    .filter((option) => option.value)
    .filter((option) => {
      const key = `${option.value.toLowerCase()}::${option.label.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export default function SalesReportsView({
  user = null,
  searchIntent = null,
  onSearchIntentHandled = null,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ms" ? "ms-MY" : "en-MY";
  const { currency } = useCurrencyFormatter();
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showExportOverlay, setShowExportOverlay] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [hoveredSaleId, setHoveredSaleId] = useState(null);
  const [correctionSaleId, setCorrectionSaleId] = useState(null);
  const [deleteSaleTarget, setDeleteSaleTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    preset: "all",
    startDate: "",
    endDate: "",
  });
  const { showSuccess, showError } = useNotification();
  const exportRangeOptions = [
    {
      value: "all",
      label: t("admin.salesReports.exportModal.all"),
      description: t("admin.salesReports.exportModal.allDesc"),
    },
    {
      value: "thisMonth",
      label: t("admin.salesReports.exportModal.thisMonth"),
      description: t("admin.salesReports.exportModal.thisMonthDesc"),
    },
    {
      value: "lastMonth",
      label: t("admin.salesReports.exportModal.lastMonth"),
      description: t("admin.salesReports.exportModal.lastMonthDesc"),
    },
    {
      value: "thisYear",
      label: t("admin.salesReports.exportModal.thisYear"),
      description: t("admin.salesReports.exportModal.thisYearDesc"),
    },
    {
      value: "lastYear",
      label: t("admin.salesReports.exportModal.lastYear"),
      description: t("admin.salesReports.exportModal.lastYearDesc"),
    },
    {
      value: "custom",
      label: t("admin.salesReports.exportModal.custom"),
      description: t("admin.salesReports.exportModal.customDesc"),
    },
  ];

  // Calculate default dates: last 3 days to today (for future use)
  // const getDefaultDates = () => {
  //   const today = new Date();
  //   const threeDaysAgo = new Date(today);
  //   threeDaysAgo.setDate(today.getDate() - 3);
  //
  //   return {
  //     startDate: threeDaysAgo.toISOString().split('T')[0],
  //     endDate: today.toISOString().split('T')[0]
  //   };
  // };

  // const defaultDates = getDefaultDates();

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    userId: "",
    category: [],
    promoter: [],
    location: [],
    product: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    promoter: [],
    location: [],
    product: [],
    category: [],
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    limit: 10,
    offset: 0,
    total: 0,
  });
  const DEFAULT_SERVER_SORT = useMemo(
    () => ({ key: "sale_date", direction: "desc" }),
    [],
  );
  const [serverSort, setServerSort] = useState(DEFAULT_SERVER_SORT);

  const salesColumns = useMemo(
    () => [
      {
        label: t("admin.salesReports.table.dateTime"),
        sortKey: "sale_date",
        sortValue: (sale) => toMs(sale?.sale_date),
      },
      {
        label: "Type",
        sortKey: null,
        sortValue: (sale) => toSafeString(getSaleTypeLabel(sale)),
      },
      {
        label: t("admin.salesReports.table.salesperson"),
        sortKey: "salesperson",
        sortValue: (sale) => toSafeString(sale?.user?.full_name),
      },
      {
        label: t("admin.salesReports.table.location"),
        sortKey: "location",
        sortValue: (sale) =>
          toSafeString(
            sale?.outlet?.bp_outlet_name || sale?.checkIn?.outlet_name,
          ),
      },
      {
        label: t("admin.salesReports.table.category"),
        sortKey: "category",
        sortValue: getSaleCategorySortValue,
      },
      {
        label: t("admin.salesReports.table.product"),
        sortKey: "product",
        sortValue: getSaleProductSortValue,
      },
      {
        label: t("admin.salesReports.table.variant"),
        sortKey: "variant",
        sortValue: getSaleVariantSortValue,
      },
      {
        label: t("admin.salesReports.table.qty"),
        sortKey: "qty",
        sortValue: getSaleQtySortValue,
      },
      {
        label: t("admin.salesReports.table.total"),
        sortKey: "total_amount",
        sortValue: (sale) => Number.parseFloat(sale?.total_amount) || 0,
      },
      {
        label: t("admin.salesReports.table.receipt"),
        sortKey: "receipt",
        sortValue: (sale) => (sale?.receipt_image_url ? 1 : 0),
      },
      {
        label: t("admin.salesReports.table.action", { defaultValue: "Action" }),
        sortKey: null,
      },
    ],
    [t],
  );

  // Debounce timer for search
  const searchTimeoutRef = useRef(null);
  const hasSkippedInitialSearchFetchRef = useRef(false);
  const summarySignatureRef = useRef(null);
  const optionsTimeoutRef = useRef({});
  const loadedOptionTypesRef = useRef(new Set());
  const lastOptionsQueryRef = useRef({});
  const optionQueryCacheRef = useRef({});
  const latestOptionsRequestRef = useRef({});
  const optionsScopeVersionRef = useRef(0);
  const MIN_OPTION_QUERY_LENGTH = 2;
  const OPTION_QUERY_DEBOUNCE_MS = 120;
  const DEFAULT_OPTIONS_LIMIT = 200;
  const QUERY_OPTIONS_LIMIT = 20;

  // Fetch sales data when filters or pagination changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.startDate,
    filters.endDate,
    filters.userId,
    filters.category,
    filters.promoter,
    filters.location,
    filters.product,
    currency,
    pagination.offset,
    pagination.limit,
    serverSort.key,
    serverSort.direction,
  ]);

  // Debounced search effect
  useEffect(() => {
    if (!hasSkippedInitialSearchFetchRef.current) {
      hasSkippedInitialSearchFetchRef.current = true;
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchSalesData({ forceIncludeSummary: true });
    }, 250); // 250ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const buildFiltersFromSearchIntent = useCallback((intent) => {
    const query = String(intent?.query || "").trim();
    const scope = String(intent?.scope || "").toLowerCase();
    const item = intent?.item || null;

    const nextFilters = {
      search: "",
      startDate: "",
      endDate: "",
      userId: "",
      category: [],
      promoter: [],
      location: [],
      product: [],
    };

    if (scope === "users") {
      const promoterValue = String(item?.label || item?.email || query).trim();
      if (promoterValue) nextFilters.promoter = [promoterValue];
      return nextFilters;
    }

    if (scope === "products") {
      const productValue = String(
        item?.label || item?.product_description || item?.product_code || query,
      ).trim();
      if (productValue) nextFilters.product = [productValue];
      return nextFilters;
    }

    if (scope === "outlets") {
      const locationValue = String(
        item?.label || item?.name || item?.code || query,
      ).trim();
      if (locationValue) nextFilters.location = [locationValue];
      return nextFilters;
    }

    if (scope === "sales" || scope === "sessions") {
      const promoterValue = String(item?.promoter_name || "").trim();
      const locationValue = String(item?.outlet_name || "").trim();
      if (promoterValue) nextFilters.promoter = [promoterValue];
      if (locationValue) nextFilters.location = [locationValue];
      if (!promoterValue && !locationValue && query) {
        nextFilters.search = query;
      }
      return nextFilters;
    }

    if (query) {
      nextFilters.search = query;
    }

    return nextFilters;
  }, []);

  useEffect(() => {
    if (!searchIntent) return;

    const nextFilters = buildFiltersFromSearchIntent(searchIntent);
    setFilters(nextFilters);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
    setServerSort(DEFAULT_SERVER_SORT);

    if (typeof onSearchIntentHandled === "function" && searchIntent.intentId) {
      onSearchIntentHandled(searchIntent.intentId);
    }
  }, [
    DEFAULT_SERVER_SORT,
    buildFiltersFromSearchIntent,
    onSearchIntentHandled,
    searchIntent,
  ]);

  const buildScopeForType = useCallback(
    (type) => {
      const scope = {
        userId: filters.userId || "",
        startDate: filters.startDate || "",
        endDate: filters.endDate || "",
        search: filters.search || "",
        promoter: toFilterArray(filters.promoter),
        location: toFilterArray(filters.location),
      };

      // Avoid self-filtering suggestion lists by their currently selected value.
      if (type === "promoter") {
        scope.promoter = [];
      } else if (type === "location") {
        scope.location = [];
      }

      return scope;
    },
    [
      filters.userId,
      filters.startDate,
      filters.endDate,
      filters.search,
      filters.promoter,
      filters.location,
    ],
  );

  const buildScopeCacheKey = (scope) =>
    JSON.stringify({
      userId: scope.userId || "",
      startDate: scope.startDate || "",
      endDate: scope.endDate || "",
      search: scope.search || "",
      promoter: toFilterArray(scope.promoter),
      location: toFilterArray(scope.location),
    });

  const buildQueryCacheKey = (type, query = "", scopeKey = "") =>
    `${type}::${String(query || "")
      .trim()
      .toLowerCase()}::${scopeKey}`;

  const setTypeOptions = (type, options) => {
    const normalizedOptions = dedupeFilterOptions(type, options);
    setFilterOptions((prev) => ({
      ...prev,
      [type]: normalizedOptions,
    }));
  };

  const fetchOptions = async (
    type,
    query = "",
    limit = QUERY_OPTIONS_LIMIT,
  ) => {
    const normalizedQuery = String(query || "").trim();
    const scope = buildScopeForType(type);
    const scopeKey = buildScopeCacheKey(scope);
    const queryKey = buildQueryCacheKey(type, normalizedQuery, scopeKey);

    if (optionQueryCacheRef.current[queryKey]) {
      setTypeOptions(type, optionQueryCacheRef.current[queryKey]);
      return optionQueryCacheRef.current[queryKey];
    }

    const requestId = (latestOptionsRequestRef.current[type] || 0) + 1;
    latestOptionsRequestRef.current[type] = requestId;
    const requestScopeVersion = optionsScopeVersionRef.current;

    try {
      const response = await getSalesFilterOptions({
        type,
        q: normalizedQuery,
        limit,
        ...scope,
      });
      const options = response?.data || [];

      const dedupedOptions = dedupeFilterOptions(type, options);
      optionQueryCacheRef.current[queryKey] = dedupedOptions;

      if (
        latestOptionsRequestRef.current[type] === requestId &&
        requestScopeVersion === optionsScopeVersionRef.current
      ) {
        setTypeOptions(type, dedupedOptions);
      }

      return dedupedOptions;
    } catch (error) {
      console.error(`Failed to fetch ${type} options:`, error);
      return [];
    }
  };

  useEffect(() => {
    const timers = optionsTimeoutRef.current;
    return () => {
      Object.values(timers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  useEffect(() => {
    // Invalidate late async helper responses from previous filter scopes.
    optionsScopeVersionRef.current += 1;

    const scopedOptions = {};
    ["promoter", "location", "product", "category"].forEach((type) => {
      const scopeKey = buildScopeCacheKey(buildScopeForType(type));
      const baseKey = buildQueryCacheKey(type, "", scopeKey);
      scopedOptions[type] = (optionQueryCacheRef.current[baseKey] || []).slice(
        0,
        QUERY_OPTIONS_LIMIT,
      );
    });

    setFilterOptions((prev) => ({
      ...prev,
      ...scopedOptions,
    }));
    lastOptionsQueryRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.userId,
    filters.startDate,
    filters.endDate,
    filters.search,
    filters.promoter,
    filters.location,
  ]);

  const buildSummarySignature = () =>
    JSON.stringify({
      search: filters.search || "",
      startDate: filters.startDate || "",
      endDate: filters.endDate || "",
      userId: filters.userId || "",
      category: Array.isArray(filters.category) ? filters.category : [],
      promoter: toFilterArray(filters.promoter),
      location: toFilterArray(filters.location),
      product: Array.isArray(filters.product) ? filters.product : [],
      currency,
    });

  const fetchSalesData = async ({ forceIncludeSummary = false } = {}) => {
    const filterSignature = buildSummarySignature();
    const includeSummary =
      forceIncludeSummary || summarySignatureRef.current !== filterSignature;

    setLoading(true);
    setError(null);
    if (includeSummary && !summary) {
      setSummaryLoading(true);
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setError(t("admin.common.notAuthenticated"));
      setLoading(false);
      if (!summary) setSummaryLoading(false);
      return;
    }

    try {
      const response = await getAllSales({
        search: filters.search,
        startDate: filters.startDate,
        endDate: filters.endDate,
        userId: filters.userId,
        category: filters.category,
        promoter: toFilterArray(filters.promoter),
        location: toFilterArray(filters.location),
        product: filters.product,
        currency,
        includeSummary,
        sortBy: serverSort.key,
        sortDirection: serverSort.direction,
        limit: pagination.limit,
        offset: pagination.offset,
      });

      setSalesData(response.data);
      if (response.summary) {
        setSummary(response.summary);
        summarySignatureRef.current = filterSignature;
        setSummaryLoading(false);
      }
      setPagination((prev) => ({
        ...prev,
        total: response.pagination.total,
      }));
      setInitialLoading(false);
    } catch (err) {
      console.error("Error fetching sales:", err);
      if (err.response?.status === 401) {
        setError(t("admin.common.authFailed"));
      } else if (err.response?.status === 403) {
        setError(t("admin.common.accessDenied"));
      } else {
        showError(localizeApiError(t, err, "admin.common.failedToLoadSales"));
      }
      if (!summary) setSummaryLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    setShowExportModal(true);
  };

  const getDateRangeFromPreset = (preset) => {
    const today = new Date();
    let startDate = "";
    let endDate = today.toISOString().split("T")[0];

    switch (preset) {
      case "thisMonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split("T")[0];
        break;
      case "lastMonth":
        const lastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        startDate = lastMonth.toISOString().split("T")[0];
        endDate = new Date(today.getFullYear(), today.getMonth(), 0)
          .toISOString()
          .split("T")[0];
        break;
      case "thisYear":
        startDate = new Date(today.getFullYear(), 0, 1)
          .toISOString()
          .split("T")[0];
        break;
      case "lastYear":
        startDate = new Date(today.getFullYear() - 1, 0, 1)
          .toISOString()
          .split("T")[0];
        endDate = new Date(today.getFullYear() - 1, 11, 31)
          .toISOString()
          .split("T")[0];
        break;
      case "custom":
        return {
          startDate: exportDateRange.startDate,
          endDate: exportDateRange.endDate,
        };
      case "all":
      default:
        startDate = "";
        endDate = "";
        break;
    }

    return { startDate, endDate };
  };

  const handleConfirmExport = async () => {
    const dateRange = getDateRangeFromPreset(exportDateRange.preset);

    setExporting(true);
    setShowExportModal(false);
    setShowExportOverlay(true);
    try {
      const response = await exportSalesToCSV({
        search: filters.search,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        userId: filters.userId,
        category: filters.category,
        promoter: toFilterArray(filters.promoter),
        location: toFilterArray(filters.location),
        product: filters.product,
        currency,
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `sales_report_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(t("admin.salesReports.exportSuccess"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      showError(t("admin.salesReports.exportFailed"));
    } finally {
      setExporting(false);
      setShowExportOverlay(false);
    }
  };

  const handleViewReceipt = (receiptUrl) => {
    if (receiptUrl) {
      window.open(resolveAssetUrl(receiptUrl), "_blank", "noopener,noreferrer");
    } else {
      showError(t("admin.salesReports.noReceipt"));
    }
  };

  const isSuperAdmin = user?.role === "super_admin";

  const handleDeleteSaleConfirm = async () => {
    if (!deleteSaleTarget?.sale_id) return;

    try {
      setDeleteSubmitting(true);
      await deleteAdminSaleById(deleteSaleTarget.sale_id);
      showSuccess(
        t("admin.salesReports.corrections.deleteSuccess", {
          defaultValue: "Sale deleted successfully.",
        }),
      );
      setDeleteSaleTarget(null);
      await fetchSalesData({ forceIncludeSummary: true });
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.salesReports.corrections.deleteFailed", {
            defaultValue: "Failed to delete sale.",
          }),
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleFilterQueryChange = (type, value) => {
    if (optionsTimeoutRef.current[type]) {
      clearTimeout(optionsTimeoutRef.current[type]);
    }

    const normalizedValue = value.trim();
    const scopeKey = buildScopeCacheKey(buildScopeForType(type));
    const baseKey = buildQueryCacheKey(type, "", scopeKey);
    const baseOptions = optionQueryCacheRef.current[baseKey] || [];

    if (normalizedValue.length < MIN_OPTION_QUERY_LENGTH) {
      lastOptionsQueryRef.current[type] = normalizedValue;

      if (!normalizedValue) {
        if (baseOptions.length > 0) {
          setTypeOptions(type, baseOptions.slice(0, QUERY_OPTIONS_LIMIT));
        } else if (loadedOptionTypesRef.current.has(type)) {
          fetchOptions(type, "", DEFAULT_OPTIONS_LIMIT);
        }
        return;
      }

      if (baseOptions.length > 0) {
        const lowered = normalizedValue.toLowerCase();
        setTypeOptions(
          type,
          baseOptions
            .filter((option) =>
              `${option.value || ""} ${option.label || ""}`
                .toLowerCase()
                .includes(lowered),
            )
            .slice(0, QUERY_OPTIONS_LIMIT),
        );
      } else {
        setTypeOptions(type, []);
      }
      return;
    }

    if (lastOptionsQueryRef.current[type] === normalizedValue) {
      return;
    }

    if (baseOptions.length > 0) {
      const lowered = normalizedValue.toLowerCase();
      setTypeOptions(
        type,
        baseOptions
          .filter((option) =>
            `${option.value || ""} ${option.label || ""}`
              .toLowerCase()
              .includes(lowered),
          )
          .slice(0, QUERY_OPTIONS_LIMIT),
      );
    }

    lastOptionsQueryRef.current[type] = normalizedValue;

    optionsTimeoutRef.current[type] = setTimeout(() => {
      fetchOptions(type, normalizedValue, QUERY_OPTIONS_LIMIT);
    }, OPTION_QUERY_DEBOUNCE_MS);
  };

  const handleFilterFieldFocus = (type) => {
    const baseKey = buildQueryCacheKey(
      type,
      "",
      buildScopeCacheKey(buildScopeForType(type)),
    );

    if (
      loadedOptionTypesRef.current.has(type) &&
      optionQueryCacheRef.current[baseKey]
    ) {
      return;
    }

    loadedOptionTypesRef.current.add(type);
    fetchOptions(type, "", DEFAULT_OPTIONS_LIMIT);
  };

  const handleSearchSubmit = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setPagination((prev) => ({ ...prev, offset: 0 }));
    fetchSalesData({ forceIncludeSummary: true });
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      startDate: "",
      endDate: "",
      userId: "",
      category: [],
      promoter: [],
      location: [],
      product: [],
    });
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleServerSortChange = useCallback(
    ({ key, direction }) => {
      const hasValidSort = Boolean(key) && Boolean(direction);
      const normalizedDirection = String(direction || "").toLowerCase();
      const nextSort =
        hasValidSort &&
        (normalizedDirection === "asc" || normalizedDirection === "desc")
          ? { key, direction: normalizedDirection }
          : DEFAULT_SERVER_SORT;

      setServerSort(nextSort);
      setPagination((previous) => ({
        ...previous,
        offset: 0,
      }));
    },
    [DEFAULT_SERVER_SORT],
  );

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.startDate ||
    filters.endDate ||
    filters.userId ||
    (Array.isArray(filters.promoter) && filters.promoter.length > 0) ||
    (Array.isArray(filters.location) && filters.location.length > 0) ||
    (Array.isArray(filters.product) && filters.product.length > 0) ||
    (Array.isArray(filters.category) && filters.category.length > 0),
  );

  if (error) {
    return (
      <div className="admin-sales-reports-content">
        <div className="admin-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-sales-reports-content">
      <ReportsHeader
        onExportCSV={handleExportCSV}
        exporting={exporting}
        t={t}
      />

      {summary ? (
        <SummaryCards summary={summary} t={t} locale={locale} currency={currency} />
      ) : summaryLoading ? (
        <SummaryCardsSkeleton />
      ) : null}

      <ReportsFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        onSearch={handleSearchSubmit}
        onFilterQueryChange={handleFilterQueryChange}
        onFilterFieldFocus={handleFilterFieldFocus}
        filterOptions={filterOptions}
        pagination={pagination}
        onLimitChange={(newLimit) =>
          setPagination((prev) => ({ ...prev, offset: 0, limit: newLimit }))
        }
        loading={initialLoading}
        t={t}
      />

      {(loading || salesData.length > 0) && (
        <DataTable
          data={salesData}
          columns={salesColumns}
          renderRow={(sale, index) => (
            <SalesTableRows
              key={`sale-${sale.sale_id}-${index}`}
              sale={sale}
              onViewReceipt={handleViewReceipt}
              canCorrect={isSuperAdmin}
              onEditSale={(targetSale) => {
                setCorrectionSaleId(targetSale.sale_id);
              }}
              onDeleteSale={(targetSale) => {
                setDeleteSaleTarget(targetSale);
              }}
              t={t}
              locale={locale}
              hoveredSaleId={hoveredSaleId}
              onHoverChange={setHoveredSaleId}
            />
          )}
          showHeader={false}
          showSearch={false}
          loading={loading}
          tableClassName="admin-reports-table"
          containerClassName="admin-reports-table-container"
          defaultSortKey="sale_date"
          defaultSortDirection="desc"
          serverMode
          serverSort={serverSort}
          onServerSortChange={handleServerSortChange}
          serverPagination={{
            total: pagination.total,
            limit: pagination.limit,
            offset: pagination.offset,
          }}
          onServerPaginationChange={({ limit, offset }) =>
            setPagination((previous) => ({
              ...previous,
              limit,
              offset,
            }))
          }
        />
      )}

      <SaleCorrectionDrawer
        isOpen={Boolean(correctionSaleId)}
        saleId={correctionSaleId}
        onClose={() => {
          setCorrectionSaleId(null);
        }}
        onSaved={() => fetchSalesData({ forceIncludeSummary: true })}
        t={t}
        showSuccess={showSuccess}
        showError={showError}
      />

      <Modal
        isOpen={Boolean(deleteSaleTarget)}
        onClose={() => {
          if (deleteSubmitting) return;
          setDeleteSaleTarget(null);
        }}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.salesReports.corrections.deleteTitle", { defaultValue: "Delete Sale" })}
          message={t("admin.salesReports.corrections.deletePrompt", {
            defaultValue: "This will permanently remove the sale and its items.",
          })}
          variant="danger"
          confirmText={t("admin.common.delete", { defaultValue: "Delete" })}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.deleting", { defaultValue: "Deleting..." })}
          onConfirm={handleDeleteSaleConfirm}
          onCancel={() => {
            if (deleteSubmitting) return;
            setDeleteSaleTarget(null);
          }}
          isLoading={deleteSubmitting}
        />
      </Modal>

      {!loading && salesData.length === 0 && (
        <EmptyState
          hasFilters={hasActiveFilters}
          onClearFilters={clearFilters}
          t={t}
        />
      )}

      {/* Export CSV Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        size="medium"
      >
        <div className="admin-reports-export-modal">
          <h3 className="admin-reports-export-title">
            {t("admin.salesReports.exportModal.title")}
          </h3>
          <p className="admin-reports-export-subtitle">
            {t("admin.salesReports.exportModal.subtitle")}
          </p>

          <div className="admin-reports-export-options">
            {exportRangeOptions.map((option) => (
              <label
                key={option.value}
                className={`admin-reports-export-option${
                  exportDateRange.preset === option.value ? " is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="exportRange"
                  value={option.value}
                  checked={exportDateRange.preset === option.value}
                  onChange={(e) =>
                    setExportDateRange((prev) => ({
                      ...prev,
                      preset: e.target.value,
                    }))
                  }
                  className="admin-reports-export-radio"
                />
                <div className="admin-reports-export-option-content">
                  <div className="admin-reports-export-option-label">
                    {option.label}
                  </div>
                  <div className="admin-reports-export-option-desc">
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {exportDateRange.preset === "custom" && (
            <div className="admin-reports-export-custom-range">
              <div className="admin-reports-export-field">
                <label className="admin-reports-export-field-label">
                  {t("admin.salesReports.exportModal.startDate")}
                </label>
                <input
                  type="date"
                  value={exportDateRange.startDate}
                  onChange={(e) =>
                    setExportDateRange((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  className="admin-reports-export-date-input"
                />
              </div>
              <div className="admin-reports-export-field">
                <label className="admin-reports-export-field-label">
                  {t("admin.salesReports.exportModal.endDate")}
                </label>
                <input
                  type="date"
                  value={exportDateRange.endDate}
                  onChange={(e) =>
                    setExportDateRange((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  className="admin-reports-export-date-input"
                />
              </div>
            </div>
          )}

          <div className="admin-reports-export-actions">
            <button
              className="admin-btn-secondary"
              onClick={() => setShowExportModal(false)}
              disabled={exporting}
            >
              {t("admin.common.cancel")}
            </button>
            <button
              className="admin-btn-primary admin-reports-export-confirm-btn"
              onClick={handleConfirmExport}
              disabled={
                exporting ||
                (exportDateRange.preset === "custom" &&
                  (!exportDateRange.startDate || !exportDateRange.endDate))
              }
            >
              {exporting
                ? t("admin.salesReports.exporting")
                : t("admin.salesReports.exportCsv")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Export Loading Overlay */}
      {showExportOverlay && (
        <div className="admin-reports-export-overlay">
          <div className="admin-reports-export-overlay-card">
            <div className="admin-reports-export-overlay-spinner"></div>
            <div className="admin-reports-export-overlay-copy">
              <h3 className="admin-reports-export-overlay-title">
                {t("admin.salesReports.exportOverlay.title")}
              </h3>
              <p className="admin-reports-export-overlay-subtitle">
                {t("admin.salesReports.exportOverlay.subtitle")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportsHeader({ onExportCSV, exporting, t }) {
  return (
    <div className="admin-reports-header">
      <div className="admin-reports-header-left">
        <h2 className="admin-section-title">{t("admin.salesReports.title")}</h2>
        <p className="admin-section-subtitle">
          {t("admin.salesReports.subtitle")}
        </p>
      </div>
      <div className="admin-reports-actions">
        <button
          className="admin-btn-primary"
          onClick={onExportCSV}
          disabled={exporting}
        >
          <Download size={16} />
          {exporting
            ? t("admin.salesReports.exporting")
            : t("admin.salesReports.exportCsv")}
        </button>
      </div>
    </div>
  );
}

function SummaryCards({ summary, t, locale, currency }) {
  const { format: formatCurrency } = useCurrencyFormatter();
  const summaryCurrency = summary.currency || currency;
  return (
    <div className="reports-summary-grid">
      <div className="summary-card">
        <div className="summary-card-icon summary-card-icon--blue">
          <ShoppingCart size={22} />
        </div>
        <div className="summary-card-content">
          <p className="summary-label">
            {t("admin.salesReports.summary.totalSales")}
          </p>
          <h3 className="summary-value">{summary.totalSales}</h3>
          <p className="summary-subtext">
            {t("admin.salesReports.summary.transactionsRecorded")}
          </p>
        </div>
      </div>

      <div className="summary-card">
        <div className="summary-card-icon summary-card-icon--green">
          <Wallet size={22} />
        </div>
        <div className="summary-card-content">
          <p className="summary-label">
            {t("admin.salesReports.summary.totalRevenue")}
          </p>
          <h3 className="summary-value">
            {formatCurrency(summary.totalAmount, new Date(), summaryCurrency)}
          </h3>
          <p className="summary-subtext">
            {t("admin.salesReports.summary.grossSalesAmount")}
          </p>
        </div>
      </div>

      <div className="summary-card">
        <div className="summary-card-icon summary-card-icon--amber">
          <TrendingUp size={22} />
        </div>
        <div className="summary-card-content">
          <p className="summary-label">
            {t("admin.salesReports.summary.averageSale")}
          </p>
          <h3 className="summary-value">
            {formatCurrency(summary.averageSaleAmount, new Date(), summaryCurrency)}
          </h3>
          <p className="summary-subtext">
            {t("admin.salesReports.summary.perTransaction")}
          </p>
        </div>
      </div>

      <div className="summary-card">
        <div className="summary-card-icon summary-card-icon--purple">
          <Users size={22} />
        </div>
        <div className="summary-card-content">
          <p className="summary-label">
            {t("admin.salesReports.summary.totalItems")}
          </p>
          <h3 className="summary-value">{summary.totalItems || 0}</h3>
          <p className="summary-subtext">
            {t("admin.salesReports.summary.productsSold")}
          </p>
        </div>
      </div>
    </div>
  );
}

function SummaryCardsSkeleton() {
  return (
    <div className="reports-summary-grid">
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="summary-card skeleton-card">
          <div className="skeleton-icon"></div>
          <div className="summary-card-content">
            <div className="skeleton-text skeleton-label"></div>
            <div className="skeleton-text skeleton-value"></div>
            <div className="skeleton-text skeleton-subtext"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsFilters({
  filters,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
  onSearch,
  onFilterQueryChange,
  onFilterFieldFocus,
  filterOptions,
  pagination,
  onLimitChange,
  loading,
  t,
}) {
  const isFilled = (value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    location: toFilterArray(filters.location),
    promoter: toFilterArray(filters.promoter),
    product: Array.isArray(filters.product) ? filters.product : [],
    category: Array.isArray(filters.category) ? filters.category : [],
  });
  const [draftLocationInput, setDraftLocationInput] = useState("");
  const [draftPromoterInput, setDraftPromoterInput] = useState("");
  const [draftProductInput, setDraftProductInput] = useState("");
  const [draftCategoryInput, setDraftCategoryInput] = useState("");

  useEffect(() => {
    setDraftFilters({
      location: toFilterArray(filters.location),
      promoter: toFilterArray(filters.promoter),
      product: Array.isArray(filters.product) ? filters.product : [],
      category: Array.isArray(filters.category) ? filters.category : [],
    });
    setDraftLocationInput("");
    setDraftPromoterInput("");
    setDraftProductInput("");
    setDraftCategoryInput("");
  }, [filters.location, filters.promoter, filters.product, filters.category]);

  const applyTagDraftFilter = (key, inputValue) => {
    const normalizedInput = String(inputValue || "").trim();
    const existingTags = Array.isArray(draftFilters[key])
      ? draftFilters[key]
      : [];
    const hasExisting = existingTags.some(
      (tag) =>
        String(tag || "").toLowerCase() === normalizedInput.toLowerCase(),
    );

    const nextTags =
      normalizedInput && !hasExisting
        ? [...existingTags, normalizedInput]
        : existingTags;

    setDraftFilters((prev) => ({ ...prev, [key]: nextTags }));
    onFilterChange(key, nextTags);

    if (key === "location") setDraftLocationInput("");
    if (key === "promoter") setDraftPromoterInput("");
    if (key === "product") setDraftProductInput("");
    if (key === "category") setDraftCategoryInput("");

    // Reset helper scope to unfiltered options after applying a tag filter.
    onFilterQueryChange(key, "");
  };

  const advancedFilterCount = [
    filters.location,
    filters.product,
    filters.promoter,
    filters.category,
  ].filter(isFilled).length;

  const hasAdvancedFilters =
    isFilled(filters.location) ||
    isFilled(filters.product) ||
    isFilled(filters.promoter) ||
    isFilled(filters.category);

  const productChips = (Array.isArray(filters.product) ? filters.product : [])
    .filter(Boolean)
    .map((value, index) => ({
      key: `product-${index}`,
      filterKey: "product",
      filterValue: value,
      label: t("admin.salesReports.filters.product", { value }),
    }));

  const categoryChips = (
    Array.isArray(filters.category) ? filters.category : []
  )
    .filter(Boolean)
    .map((value, index) => ({
      key: `category-${index}`,
      filterKey: "category",
      filterValue: value,
      label: t("admin.salesReports.filters.category", { value }),
    }));

  const locationChips = toFilterArray(filters.location).map((value, index) => ({
    key: `location-${index}`,
    filterKey: "location",
    filterValue: value,
    label: t("admin.salesReports.filters.outlet", { value }),
  }));

  const promoterChips = toFilterArray(filters.promoter).map((value, index) => ({
    key: `promoter-${index}`,
    filterKey: "promoter",
    filterValue: value,
    label: t("admin.salesReports.filters.promoter", { value }),
  }));

  const activeFilterChips = [
    filters.search
      ? {
          key: "search",
          filterKey: "search",
          label: t("admin.salesReports.filters.search", {
            value: filters.search,
          }),
        }
      : null,
    filters.startDate
      ? {
          key: "startDate",
          filterKey: "startDate",
          label: t("admin.salesReports.filters.from", {
            value: filters.startDate,
          }),
        }
      : null,
    filters.endDate
      ? {
          key: "endDate",
          filterKey: "endDate",
          label: t("admin.salesReports.filters.to", { value: filters.endDate }),
        }
      : null,
    ...locationChips,
    ...productChips,
    ...promoterChips,
    ...categoryChips,
  ].filter(Boolean);

  const handleClearSingleFilter = (chip) => {
    const key = chip?.filterKey || chip?.key;
    if (
      key === "product" ||
      key === "category" ||
      key === "location" ||
      key === "promoter"
    ) {
      const current = Array.isArray(filters[key]) ? filters[key] : [];
      if (chip?.filterValue) {
        const next = current.filter((value) => value !== chip.filterValue);
        onFilterChange(key, next);
        return;
      }
      onFilterChange(key, []);
      return;
    }
    onFilterChange(key, "");
  };

  return (
    <div className="admin-reports-filters">
      <div className="filters-primary-row">
        <div className="search-container">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder={t("admin.common.searchSales")}
            className="modern-input"
            value={filters.search}
            onChange={(e) => onFilterChange("search", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
            disabled={loading}
          />
        </div>
      </div>

      <div className="filters-secondary-row">
        <div className="date-group">
          <input
            type="date"
            className="date-input"
            value={filters.startDate}
            onChange={(e) => onFilterChange("startDate", e.target.value)}
            placeholder={t("admin.salesReports.exportModal.startDate")}
            disabled={loading}
          />
          <span className="date-separator">→</span>
          <input
            type="date"
            className="date-input"
            value={filters.endDate}
            onChange={(e) => onFilterChange("endDate", e.target.value)}
            placeholder={t("admin.salesReports.exportModal.endDate")}
            disabled={loading}
          />
        </div>

        <button
          type="button"
          className={`filters-toggle-btn ${showAdvancedFilters ? "active" : ""}`}
          onClick={() => setShowAdvancedFilters((prev) => !prev)}
          disabled={loading}
        >
          <SlidersHorizontal size={16} />
          <span>{t("admin.common.filters")}</span>
          {advancedFilterCount > 0 && (
            <span className="filters-count">{advancedFilterCount}</span>
          )}
          {showAdvancedFilters ? (
            <ChevronUp size={16} />
          ) : (
            <ChevronDown size={16} />
          )}
        </button>

        <div className="items-per-page">
          <label htmlFor="itemsPerPage">{t("admin.common.itemsPerPage")}</label>
          <select
            id="itemsPerPage"
            value={pagination.limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="items-per-page-select"
            disabled={loading}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {showAdvancedFilters && (
        <div className="advanced-filters-panel">
          <div className="advanced-filters-grid">
            <label className="advanced-filter-field">
              <span>{t("admin.salesReports.filters.bpOutlet")}</span>
              <div className="filter-input-row">
                <TagInput
                  placeholder={t("admin.salesReports.filters.filterByOutlet")}
                  tags={draftFilters.location}
                  inputValue={draftLocationInput}
                  listId="location-options"
                  disabled={loading}
                  onFocus={() => onFilterFieldFocus("location")}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({ ...prev, location: value }))
                  }
                  onInputValueChange={(value) => setDraftLocationInput(value)}
                  onQueryChange={(value) =>
                    onFilterQueryChange("location", value)
                  }
                  onCommit={(nextTags) => onFilterChange("location", nextTags)}
                />
                <button
                  type="button"
                  className="filter-apply-btn"
                  onClick={() =>
                    applyTagDraftFilter("location", draftLocationInput)
                  }
                  disabled={loading}
                >
                  Apply
                </button>
              </div>
            </label>
            <datalist id="location-options">
              {filterOptions.location.map((option, index) => (
                <option key={`location-${index}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </datalist>

            <label className="advanced-filter-field">
              <span>{t("admin.salesReports.filters.productLabel")}</span>
              <div className="filter-input-row">
                <TagInput
                  placeholder={t("admin.salesReports.filters.filterByProduct")}
                  tags={draftFilters.product}
                  inputValue={draftProductInput}
                  listId="product-options"
                  disabled={loading}
                  onFocus={() => onFilterFieldFocus("product")}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({ ...prev, product: value }))
                  }
                  onInputValueChange={(value) => setDraftProductInput(value)}
                  onQueryChange={(value) =>
                    onFilterQueryChange("product", value)
                  }
                  onCommit={(nextTags) => onFilterChange("product", nextTags)}
                />
                <button
                  type="button"
                  className="filter-apply-btn"
                  onClick={() =>
                    applyTagDraftFilter("product", draftProductInput)
                  }
                  disabled={loading}
                >
                  Apply
                </button>
              </div>
            </label>
            <datalist id="product-options">
              {filterOptions.product.map((option, index) => (
                <option key={`product-${index}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </datalist>

            <label className="advanced-filter-field">
              <span>{t("admin.salesReports.filters.promoterLabel")}</span>
              <div className="filter-input-row">
                <TagInput
                  placeholder={t("admin.salesReports.filters.filterByPromoter")}
                  tags={draftFilters.promoter}
                  inputValue={draftPromoterInput}
                  listId="promoter-options"
                  disabled={loading}
                  onFocus={() => onFilterFieldFocus("promoter")}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({ ...prev, promoter: value }))
                  }
                  onInputValueChange={(value) => setDraftPromoterInput(value)}
                  onQueryChange={(value) =>
                    onFilterQueryChange("promoter", value)
                  }
                  onCommit={(nextTags) => onFilterChange("promoter", nextTags)}
                />
                <button
                  type="button"
                  className="filter-apply-btn"
                  onClick={() =>
                    applyTagDraftFilter("promoter", draftPromoterInput)
                  }
                  disabled={loading}
                >
                  Apply
                </button>
              </div>
            </label>
            <datalist id="promoter-options">
              {filterOptions.promoter.map((option, index) => (
                <option key={`promoter-${index}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </datalist>

            <label className="advanced-filter-field">
              <span>{t("admin.salesReports.filters.categoryLabel")}</span>
              <div className="filter-input-row">
                <TagInput
                  placeholder={t("admin.salesReports.filters.filterByCategory")}
                  tags={draftFilters.category}
                  inputValue={draftCategoryInput}
                  listId="category-options"
                  disabled={loading}
                  onFocus={() => onFilterFieldFocus("category")}
                  onChange={(value) =>
                    setDraftFilters((prev) => ({ ...prev, category: value }))
                  }
                  onInputValueChange={(value) => setDraftCategoryInput(value)}
                  onQueryChange={(value) =>
                    onFilterQueryChange("category", value)
                  }
                  onCommit={(nextTags) => onFilterChange("category", nextTags)}
                />
                <button
                  type="button"
                  className="filter-apply-btn"
                  onClick={() =>
                    applyTagDraftFilter("category", draftCategoryInput)
                  }
                  disabled={loading}
                >
                  Apply
                </button>
              </div>
            </label>
            <datalist id="category-options">
              {filterOptions.category.map((option, index) => (
                <option key={`category-${index}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </datalist>
          </div>

          <p className="advanced-filter-hint">
            Press Enter or click Apply for advanced filters.
          </p>

          {hasAdvancedFilters && (
            <div className="advanced-filters-actions">
              <button
                className="clear-btn"
                onClick={onClearFilters}
                title={t("admin.common.clearAllFilters")}
                disabled={loading}
              >
                <X size={16} />
                {t("admin.common.clearFilters")}
              </button>
            </div>
          )}
        </div>
      )}

      {hasActiveFilters && (
        <div className="active-filter-chips">
          {activeFilterChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className="active-filter-chip"
              onClick={() => handleClearSingleFilter(chip)}
              disabled={loading}
              title={`${t("admin.common.clear")} ${chip.label}`}
            >
              <span>{chip.label}</span>
              <X size={14} />
            </button>
          ))}
          <button
            type="button"
            className="active-filter-clear-all"
            onClick={onClearFilters}
            disabled={loading}
          >
            {t("admin.common.clearAll")}
          </button>
        </div>
      )}
    </div>
  );
}

function TagInput({
  placeholder,
  tags,
  inputValue,
  listId,
  disabled,
  onFocus,
  onChange,
  onCommit,
  onInputValueChange,
  onQueryChange,
}) {
  const addTag = (rawValue) => {
    const normalized = rawValue.trim();
    if (!normalized) return;

    const hasExisting = tags.some(
      (tag) => tag.toLowerCase() === normalized.toLowerCase(),
    );
    let nextTags = tags;
    if (!hasExisting) {
      nextTags = [...tags, normalized];
      onChange(nextTags);
    }

    onInputValueChange("");
    onQueryChange("");
    if (typeof onCommit === "function") {
      onCommit(nextTags);
    }
  };

  const removeTag = (indexToRemove) => {
    onChange(tags.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="tag-input-wrap">
      <input
        type="text"
        className="category-input"
        placeholder={placeholder}
        value={inputValue}
        autoComplete="off"
        onFocus={onFocus}
        onChange={(e) => {
          onInputValueChange(e.target.value);
          onQueryChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addTag(inputValue);
            return;
          }

          if (e.key === "Backspace" && !inputValue && tags.length > 0) {
            removeTag(tags.length - 1);
          }
        }}
        list={listId}
        disabled={disabled}
      />
    </div>
  );
}

function SalesTableRows({
  sale,
  onViewReceipt,
  canCorrect,
  onEditSale,
  onDeleteSale,
  t,
  locale,
  hoveredSaleId,
  onHoverChange,
}) {
  const { format: formatCurrency } = useCurrencyFormatter();
  const isRowHovered = hoveredSaleId === sale.sale_id;
  const rowClassName = isRowHovered ? "row-hover" : "";
  const handleMouseEnter = () => onHoverChange(sale.sale_id);
  const handleMouseLeave = () => onHoverChange(null);

  // Each sale can have multiple items, create a row for each item
  if (!sale.items || sale.items.length === 0) {
    return (
      <tr
        key={`sale-empty-${sale.sale_id}`}
        data-sale-id={sale.sale_id}
        className={rowClassName}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <td className="admin-reports-date">
          {new Date(sale.sale_date).toLocaleString(locale, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </td>
        <td className="admin-reports-type">
          <span
            className={`sale-type-badge ${
              isReturnSale(sale)
                ? "sale-type-badge--return"
                : "sale-type-badge--sale"
            }`}
          >
            {getSaleTypeLabel(sale)}
          </span>
        </td>
        <td className="admin-reports-name">
          {sale.user?.full_name || t("admin.salesReports.receiptUnavailable")}
        </td>
        <td className="admin-reports-location">
          {sale.outlet?.bp_outlet_name ||
            sale.checkIn?.outlet_name ||
            t("admin.salesReports.receiptUnavailable")}
          {sale.outlet?.address && (
            <span className="location-address">
              {sale.outlet.address.street}
              {sale.outlet.address.postcode_denorm &&
                `, ${sale.outlet.address.postcode_denorm}`}
            </span>
          )}
        </td>
        <td className="admin-reports-category">-</td>
        <td>-</td>
        <td className="admin-reports-variant">-</td>
        <td className="admin-reports-qty">0</td>
        <td className="admin-reports-total">
          {formatCurrency(sale.total_amount, sale.sale_date, sale.currency)}
        </td>
        <td>
          <button
            className="admin-btn-view"
            onClick={() =>
              onViewReceipt(sale.receipt_signed_url || sale.receipt_image_url)
            }
            disabled={!sale.receipt_image_url}
          >
            <Eye size={14} />
            {t("admin.salesReports.table.view")}
          </button>
        </td>
        <td>
          {canCorrect ? (
            <div className="admin-reports-row-actions-secondary">
              <button
                type="button"
                className="admin-btn-secondary admin-reports-inline-action"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditSale(sale);
                }}
              >
                <FilePenLine size={14} />
                {t("admin.common.edit", { defaultValue: "Edit" })}
              </button>
              <button
                type="button"
                className="admin-btn-danger admin-reports-inline-action admin-reports-inline-action-danger"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteSale(sale);
                }}
              >
                <Trash2 size={14} />
                {t("admin.common.delete", { defaultValue: "Delete" })}
              </button>
            </div>
          ) : (
            <span>-</span>
          )}
        </td>
      </tr>
    );
  }

  return sale.items.map((item, index) => (
    <tr
      key={`${sale.sale_id}-${item.sale_item_id}`}
      data-sale-id={sale.sale_id}
      className={rowClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {index === 0 ? (
        <>
          <td className="admin-reports-date" rowSpan={sale.items.length}>
            {new Date(sale.sale_date).toLocaleString(locale, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </td>
          <td className="admin-reports-type" rowSpan={sale.items.length}>
            <span
              className={`sale-type-badge ${
                isReturnSale(sale)
                  ? "sale-type-badge--return"
                  : "sale-type-badge--sale"
              }`}
            >
              {getSaleTypeLabel(sale)}
            </span>
          </td>
          <td className="admin-reports-name" rowSpan={sale.items.length}>
            {sale.user?.full_name || t("admin.salesReports.receiptUnavailable")}
          </td>
          <td className="admin-reports-location" rowSpan={sale.items.length}>
            <div className="location-content">
              <div className="location-name">
                {sale.outlet?.bp_outlet_name ||
                  sale.checkIn?.outlet_name ||
                  t("admin.salesReports.receiptUnavailable")}
              </div>
              {sale.outlet?.address && (
                <div className="location-address">
                  {sale.outlet.address.street}
                  {sale.outlet.address.postcode_denorm &&
                    `, ${sale.outlet.address.postcode_denorm}`}
                </div>
              )}
            </div>
          </td>
        </>
      ) : null}
      <td className="admin-reports-category">
        {item.product?.productGroup?.product_group_description ||
          t("admin.salesReports.receiptUnavailable")}
      </td>
      <td>
        <ProductBadge
          product={
            item.product?.product_description ||
            item.product_name ||
            t("admin.salesReports.receiptUnavailable")
          }
        />
      </td>
      <td className="admin-reports-variant">
        {[item.product?.colour, item.product?.product_model_description]
          .filter(Boolean)
          .join(" / ") ||
          item.serial_number ||
          "-"}
      </td>
      <td className="admin-reports-qty">{item.quantity}</td>
      <td className="admin-reports-total">
        {formatCurrency(item.total_price, sale.sale_date, sale.currency)}
      </td>
      {index === 0 ? (
        <td rowSpan={sale.items.length}>
          <button
            className="admin-btn-view"
            onClick={() =>
              onViewReceipt(sale.receipt_signed_url || sale.receipt_image_url)
            }
            disabled={!sale.receipt_image_url}
          >
            <Eye size={14} />
            {t("admin.salesReports.table.view")}
          </button>
        </td>
      ) : null}
      {index === 0 ? (
        <td rowSpan={sale.items.length}>
          {canCorrect ? (
            <div className="admin-reports-row-actions-secondary">
              <button
                type="button"
                className="admin-btn-secondary admin-reports-inline-action"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditSale(sale);
                }}
              >
                <FilePenLine size={14} />
                {t("admin.common.edit", { defaultValue: "Edit" })}
              </button>
              <button
                type="button"
                className="admin-btn-danger admin-reports-inline-action admin-reports-inline-action-danger"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteSale(sale);
                }}
              >
                <Trash2 size={14} />
                {t("admin.common.delete", { defaultValue: "Delete" })}
              </button>
            </div>
          ) : (
            <span>-</span>
          )}
        </td>
      ) : null}
    </tr>
  ));
}

function ProductBadge({ product }) {
  return (
    <div className="product-cell">
      <span className="product-name">{product}</span>
    </div>
  );
}

function EmptyState({ hasFilters, onClearFilters, t }) {
  return (
    <div className="admin-reports-empty-state">
      <svg
        className="empty-state-icon"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3>{t("admin.salesReports.empty.title")}</h3>
      <p>
        {hasFilters
          ? t("admin.salesReports.empty.withFilters")
          : t("admin.salesReports.empty.withoutFilters")}
      </p>
      {hasFilters && (
        <button
          className="admin-btn-primary admin-reports-empty-clear-btn"
          onClick={onClearFilters}
        >
          {t("admin.common.clearAllFilters")}
        </button>
      )}
    </div>
  );
}
